import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  MessageSquareDashed,
  Calendar,
  User,
  Inbox,
  ChevronLeft,
  ChevronRight,
  ListFilter,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";

const API_BASE = process.env.REACT_APP_API_BASE;

export default function AdminFeedbackList() {
  const navigate = useNavigate();
  const { authFetch } = useAuth();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasPrev: false,
    hasNext: false,
  });

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await authFetch(
          `${API_BASE}/admin/feedback?page=${page}&pageSize=${pageSize}`
        );
        if (res.ok) {
          const data = await res.json();
          if (!isMounted) return;
          if (Array.isArray(data)) {
            // backward compatibility: old API format
            setFeedbacks(data || []);
            setPagination((prev) => ({
              ...prev,
              page,
              pageSize,
              total: data.length,
              totalPages: 1,
              hasPrev: false,
              hasNext: false,
            }));
          } else {
            setFeedbacks(data?.items || []);
            setPagination({
              page: Number(data?.pagination?.page || page),
              pageSize: Number(data?.pagination?.pageSize || pageSize),
              total: Number(data?.pagination?.total || 0),
              totalPages: Number(data?.pagination?.totalPages || 1),
              hasPrev: Boolean(data?.pagination?.hasPrev),
              hasNext: Boolean(data?.pagination?.hasNext),
            });
          }
        } else if (res.status === 403) {
          if (isMounted) setError("คุณไม่มีสิทธิ์เข้าถึงหน้านี้");
        } else {
          if (isMounted) setError("ไม่สามารถโหลดข้อมูลข้อเสนอแนะได้");
        }
      } catch (err) {
        console.error("Failed to load feedback:", err);
        if (isMounted) setError("ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadData();
    return () => { isMounted = false; };
  }, [authFetch, page, pageSize]);

  const startItem =
    pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1;
  const endItem = Math.min(
    pagination.total,
    pagination.page * pagination.pageSize
  );

  const buildPageNumbers = () => {
    const maxButtons = 5;
    const total = Math.max(1, pagination.totalPages || 1);
    let start = Math.max(1, pagination.page - 2);
    let end = Math.min(total, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4 sticky top-0 z-10 shadow-sm">
        <button 
          onClick={() => navigate("/")}
          className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <MessageSquareDashed className="text-amber-500" size={20} />
            ข้อเสนอแนะจากผู้ใช้งาน
          </h1>
          <p className="text-xs text-slate-500 font-medium">รายการข้อเสนอแนะและปัญหาที่ผู้ใช้แจ้งเข้ามา</p>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-6 max-w-4xl w-full mx-auto space-y-6">
        
        {/* Summary Card */}
        <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-6 border border-amber-400 shadow-lg shadow-amber-500/20 text-white flex items-center justify-between relative overflow-hidden">
          <div className="absolute right-[-10%] top-[-50%] w-32 h-32 bg-white/20 rounded-full blur-xl"></div>
          <div>
            <div className="text-xs font-medium text-amber-100 uppercase tracking-wider mb-1 flex items-center gap-2">
              <Inbox size={14} />
              ข้อเสนอแนะทั้งหมด
            </div>
            <div className="text-4xl font-extrabold">
              {pagination.total}
              <span className="text-sm font-medium text-amber-200"> รายการ</span>
            </div>
          </div>
        </div>

        {/* Feedback List */}
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-3xl p-12 border border-slate-200 shadow-sm text-center">
            <h3 className="text-lg font-bold text-slate-700 mb-1">ไม่สามารถเข้าถึงข้อมูลได้</h3>
            <p className="text-sm text-slate-500">{error}</p>
          </div>
        ) : feedbacks.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 border border-slate-200 shadow-sm text-center">
            <div className="w-16 h-16 bg-slate-50 text-slate-300 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquareDashed size={32} />
            </div>
            <h3 className="text-lg font-bold text-slate-700 mb-1">ยังไม่มีข้อเสนอแนะ</h3>
            <p className="text-sm text-slate-500">ระบบจะแสดงข้อเสนอแนะจากผู้ใช้ที่นี่เมื่อมีการส่งเข้ามา</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-sm font-medium text-slate-600">
                แสดง <span className="text-slate-900 font-bold">{startItem}</span>-
                <span className="text-slate-900 font-bold">{endItem}</span> จากทั้งหมด{" "}
                <span className="text-slate-900 font-bold">{pagination.total}</span> รายการ
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center gap-1.5 text-xs text-slate-500 bg-slate-100 rounded-lg px-2 py-1">
                  <ListFilter size={13} />
                  ต่อหน้า
                </div>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    const nextSize = Number(e.target.value);
                    setPageSize(nextSize);
                    setPage(1);
                  }}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-200"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            {feedbacks.map((fb) => (
              <div key={fb.id} className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2.5 text-sm font-semibold text-slate-700">
                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100">
                      <User size={14} />
                    </div>
                    {fb.user_id}
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">
                    <Calendar size={12} className="text-slate-400" />
                    {fb.created_at}
                  </div>
                </div>
                <div className="text-slate-700 text-sm leading-relaxed bg-slate-50/80 p-4 rounded-xl border border-slate-100">
                  {fb.message.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i !== fb.message.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}

            <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center justify-between">
              <button
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={!pagination.hasPrev}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
                ก่อนหน้า
              </button>

              <div className="flex items-center gap-1.5">
                {buildPageNumbers().map((n) => (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    className={`h-9 min-w-9 px-2 rounded-lg text-sm font-semibold transition-colors ${
                      n === pagination.page
                        ? "bg-amber-500 text-white shadow-sm shadow-amber-300/70"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>

              <button
                onClick={() =>
                  setPage((prev) =>
                    Math.min(pagination.totalPages || prev, prev + 1)
                  )
                }
                disabled={!pagination.hasNext}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ถัดไป
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
